import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Square, Camera, Clock, CheckCircle, AlertCircle, Plus, Trash2, ListPlus } from 'lucide-react';
import { 
  interventions as interventionsData,
  typesIntervention,
  getTypeInterventionById,
  getCentreById,
  getPointageEnCours
} from '../../data/mockData';

function RealisationIntervention({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [interventions, setInterventions] = useState([]);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [photoAvant, setPhotoAvant] = useState(null);
  const [photoApres, setPhotoApres] = useState(null);
  const [heureDebut, setHeureDebut] = useState('');
  
  // États pour l'ajout rapide
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddList, setQuickAddList] = useState([]);
  const [quickAddType, setQuickAddType] = useState('');
  const [quickAddPlaque, setQuickAddPlaque] = useState('');
  const [quickAddMessage, setQuickAddMessage] = useState({ type: '', text: '' });
  
  // Obtenir le centre du pointage actuel
  const pointageActif = getPointageEnCours(user.id);
  const centreActuel = pointageActif ? getCentreById(pointageActif.centreId) : null;

  useEffect(() => {
    // Charger les interventions du technicien pour aujourd'hui
    const today = new Date().toISOString().split('T')[0];
    const mesInterventions = interventionsData.filter(i => 
      i.technicienId === user.id && 
      i.datePrevu === today &&
      (i.statut === 'planifiee' || i.statut === 'en_cours')
    );
    setInterventions(mesInterventions);

    // Si un ID est fourni, sélectionner cette intervention
    if (id) {
      const intervention = mesInterventions.find(i => i.id === parseInt(id));
      if (intervention) {
        setSelectedIntervention(intervention);
        if (intervention.heureDebut) {
          setHeureDebut(intervention.heureDebut);
        }
      }
    }
  }, [id, user.id]);

  const handleSelectIntervention = (intervention) => {
    setSelectedIntervention(intervention);
    setPhotoAvant(null);
    setPhotoApres(null);
    setHeureDebut('');
  };

  // Fonctions pour l'ajout rapide
  const handleQuickAddToList = (e) => {
    e.preventDefault();
    
    if (!quickAddType || !quickAddPlaque.trim()) {
      setQuickAddMessage({ type: 'error', text: 'Veuillez remplir tous les champs' });
      return;
    }

    const newItem = {
      id: Date.now(),
      typeInterventionId: parseInt(quickAddType),
      plaque: quickAddPlaque.toUpperCase().trim(),
    };

    setQuickAddList([...quickAddList, newItem]);
    setQuickAddPlaque('');
    setQuickAddMessage({ type: '', text: '' });
  };

  const handleRemoveFromQuickList = (itemId) => {
    setQuickAddList(quickAddList.filter(item => item.id !== itemId));
  };

  const handleSubmitQuickAdd = () => {
    if (quickAddList.length === 0) {
      setQuickAddMessage({ type: 'error', text: 'Ajoutez au moins une intervention' });
      return;
    }

    if (!pointageActif) {
      setQuickAddMessage({ type: 'error', text: 'Vous devez d\'abord pointer pour créer des interventions' });
      return;
    }

    // Créer toutes les interventions
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    const nouvellesInterventions = quickAddList.map((item, index) => {
      const type = getTypeInterventionById(item.typeInterventionId);
      return {
        id: interventionsData.length + index + 1,
        centreId: pointageActif.centreId,
        technicienId: user.id,
        typeInterventionId: item.typeInterventionId,
        plaque: item.plaque,
        dureeEstimee: type?.dureeEstimee || null,
        datePrevu: today,
        heurePrevu: now.toTimeString().slice(0, 5),
        dateDebut: null,
        heureDebut: null,
        dateFin: null,
        heureFin: null,
        dureeReelle: null,
        photoAvant: null,
        photoApres: null,
        commentaire: null,
        statut: 'planifiee',
        creeLe: now.toISOString(),
        creePar: user.id,
      };
    });

    console.log('Interventions créées:', nouvellesInterventions);
    
    // Ajouter les nouvelles interventions à la liste locale
    setInterventions([...interventions, ...nouvellesInterventions]);
    
    // Réinitialiser le formulaire
    setQuickAddList([]);
    setShowQuickAdd(false);
    setQuickAddMessage({ type: 'success', text: `${nouvellesInterventions.length} intervention(s) créée(s) avec succès!` });
    
    setTimeout(() => {
      setQuickAddMessage({ type: '', text: '' });
    }, 3000);
  };

  const handlePhotoAvant = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoAvant(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoApres = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoApres(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDemarrer = () => {
    const now = new Date();
    const heureActuelle = now.toTimeString().slice(0, 5);
    setHeureDebut(heureActuelle);
    
    console.log('Intervention démarrée:', {
      interventionId: selectedIntervention.id,
      heureDebut: heureActuelle,
      photoAvant: photoAvant,
    });

    alert('Intervention démarrée avec succès !');
  };

  const handleTerminer = () => {
    const now = new Date();
    const heureFin = now.toTimeString().slice(0, 5);
    
    // Calculer la durée
    const [hd, md] = heureDebut.split(':').map(Number);
    const [hf, mf] = heureFin.split(':').map(Number);
    const dureeReelle = (hf * 60 + mf) - (hd * 60 + md);

    console.log('Intervention terminée:', {
      interventionId: selectedIntervention.id,
      heureDebut: heureDebut,
      heureFin: heureFin,
      dureeReelle: dureeReelle,
      photoApres: photoApres,
    });

    alert('Intervention terminée avec succès !');
    
    // Réinitialiser et retourner à la liste
    setSelectedIntervention(null);
    setPhotoAvant(null);
    setPhotoApres(null);
    setHeureDebut('');
    navigate('/realisation');
  };

  const calculerDuree = () => {
    if (!heureDebut) return '00:00';
    
    const [hd, md] = heureDebut.split(':').map(Number);
    const now = new Date();
    const heureActuelle = now.getHours();
    const minuteActuelle = now.getMinutes();
    
    const totalMinutes = (heureActuelle * 60 + minuteActuelle) - (hd * 60 + md);
    const heures = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${heures.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  if (interventions.length === 0) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Réalisation des interventions</h1>
        </div>
        <div className="empty-state">
          <CheckCircle size={48} className="icon-muted" />
          <p>Aucune intervention prévue aujourd'hui</p>
        </div>
      </div>
    );
  }

  if (!selectedIntervention) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1>Mes interventions du jour</h1>
            <p className="text-muted">
              {centreActuel ? `Centre: ${centreActuel.nom}` : 'Pointez pour voir votre centre'}
            </p>
          </div>
          <button 
            onClick={() => setShowQuickAdd(!showQuickAdd)}
            className="btn btn-primary"
          >
            <ListPlus size={20} />
            Ajout rapide
          </button>
        </div>

        {quickAddMessage.text && (
          <div className={`alert alert-${quickAddMessage.type}`}>
            {quickAddMessage.text}
          </div>
        )}

        {/* Panneau d'ajout rapide */}
        {showQuickAdd && (
          <div className="quick-add-container">
            <div className="quick-add-header">
              <h2>Ajout rapide d'interventions</h2>
              <button 
                onClick={() => setShowQuickAdd(false)}
                className="btn btn-sm btn-secondary"
              >
                Fermer
              </button>
            </div>

            {!pointageActif && (
              <div className="alert alert-warning">
                <AlertCircle size={20} />
                Vous devez pointer pour créer des interventions. Le centre sera automatiquement celui de votre pointage.
              </div>
            )}

            <form onSubmit={handleQuickAddToList} className="quick-add-form">
              <div className="form-group">
                <label>Type de véhicule</label>
                <select
                  value={quickAddType}
                  onChange={(e) => setQuickAddType(e.target.value)}
                  required
                >
                  <option value="">Sélectionner</option>
                  {typesIntervention.filter(t => t.actif).map(type => (
                    <option key={type.id} value={type.id}>
                      {type.code} - {type.libelle}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Plaque / N° Châssis</label>
                <input
                  type="text"
                  value={quickAddPlaque}
                  onChange={(e) => setQuickAddPlaque(e.target.value)}
                  placeholder="AB-123-CD ou VIN"
                  required
                />
              </div>

              <button type="submit" className="btn btn-secondary">
                <Plus size={20} />
                Ajouter à la liste
              </button>
            </form>

            {quickAddList.length > 0 && (
              <div className="quick-add-list">
                <h3>{quickAddList.length} intervention(s) à créer</h3>
                {quickAddList.map(item => {
                  const type = getTypeInterventionById(item.typeInterventionId);
                  return (
                    <div key={item.id} className="quick-add-item">
                      <div className="quick-add-item-info">
                        {type && (
                          <span 
                            className="type-badge" 
                            style={{ backgroundColor: type.couleur }}
                          >
                            {type.code}
                          </span>
                        )}
                        <strong>{item.plaque}</strong>
                      </div>
                      <button 
                        onClick={() => handleRemoveFromQuickList(item.id)}
                        className="btn btn-sm btn-danger"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}

                <button 
                  onClick={handleSubmitQuickAdd}
                  className="btn btn-success btn-block"
                  style={{ marginTop: '1rem' }}
                  disabled={!pointageActif}
                >
                  <CheckCircle size={20} />
                  Créer les {quickAddList.length} intervention(s)
                </button>
              </div>
            )}
          </div>
        )}

        <div className="interventions-list">
          {interventions.map(intervention => {
            const type = getTypeInterventionById(intervention.typeInterventionId);
            const centre = getCentreById(intervention.centreId);
            
            return (
              <div 
                key={intervention.id} 
                className="intervention-card-mobile"
                onClick={() => handleSelectIntervention(intervention)}
              >
                <div className="intervention-header-mobile">
                  <div className="intervention-time-mobile">
                    <Clock size={20} />
                    {intervention.heurePrevu}
                  </div>
                  {type && (
                    <span 
                      className="type-badge" 
                      style={{ backgroundColor: type.couleur }}
                    >
                      {type.code}
                    </span>
                  )}
                </div>

                <div className="intervention-body-mobile">
                  <h3 className="intervention-plaque-mobile">{intervention.plaque}</h3>
                  <p className="text-muted">{centre?.nom}</p>
                  {type && (
                    <p className="text-sm">
                      <Clock size={14} className="inline" /> 
                      Durée estimée : {type.dureeEstimee} min
                    </p>
                  )}
                  {intervention.commentaire && (
                    <div className="intervention-comment-mobile">
                      <AlertCircle size={14} />
                      <p className="text-sm">{intervention.commentaire}</p>
                    </div>
                  )}
                </div>

                <div className="intervention-footer-mobile">
                  <span className={`badge ${
                    intervention.statut === 'en_cours' ? 'badge-warning' : 'badge-primary'
                  }`}>
                    {intervention.statut === 'en_cours' ? 'En cours' : 'Planifiée'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const type = getTypeInterventionById(selectedIntervention.typeInterventionId);
  const centre = getCentreById(selectedIntervention.centreId);
  const enCours = !!heureDebut;

  return (
    <div className="page-container realisation-container">
      <div className="page-header">
        <button onClick={() => setSelectedIntervention(null)} className="btn-back-mobile">
          ← Retour à la liste
        </button>
      </div>

      <div className="realisation-card">
        <div className="realisation-header">
          {type && (
            <span 
              className="type-badge type-badge-lg" 
              style={{ backgroundColor: type.couleur }}
            >
              {type.code}
            </span>
          )}
          <h1>{selectedIntervention.plaque}</h1>
          <p className="text-muted">{centre?.nom}</p>
          <p className="text-muted">
            <Clock size={16} className="inline" />
            Prévu à {selectedIntervention.heurePrevu} - Durée estimée: {selectedIntervention.dureeEstimee} min
          </p>
        </div>

        {selectedIntervention.commentaire && (
          <div className="info-box info-warning">
            <AlertCircle size={20} />
            <p>{selectedIntervention.commentaire}</p>
          </div>
        )}

        {!enCours ? (
          <div className="realisation-start">
            <h2>Démarrer l'intervention</h2>
            
            <div className="photo-section">
              <label className="photo-label">
                <Camera size={24} />
                <span>Photo avant (optionnel)</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  capture="environment"
                  onChange={handlePhotoAvant}
                  className="photo-input"
                />
              </label>
              
              {photoAvant && (
                <div className="photo-preview">
                  <img src={photoAvant} alt="Photo avant" />
                </div>
              )}
            </div>

            <button onClick={handleDemarrer} className="btn btn-primary btn-lg btn-block">
              <Play size={24} />
              Démarrer l'intervention
            </button>
          </div>
        ) : (
          <div className="realisation-progress">
            <div className="timer-section">
              <Clock size={48} className="icon-primary" />
              <div>
                <p className="text-muted">Durée écoulée</p>
                <h2 className="timer">{calculerDuree()}</h2>
                <p className="text-sm text-muted">Démarrée à {heureDebut}</p>
              </div>
            </div>

            {photoAvant && (
              <div className="photo-preview-small">
                <p className="text-sm text-muted">Photo avant</p>
                <img src={photoAvant} alt="Photo avant" />
              </div>
            )}

            <div className="photo-section">
              <label className="photo-label">
                <Camera size={24} />
                <span>Photo après (optionnel)</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  capture="environment"
                  onChange={handlePhotoApres}
                  className="photo-input"
                />
              </label>
              
              {photoApres && (
                <div className="photo-preview">
                  <img src={photoApres} alt="Photo après" />
                </div>
              )}
            </div>

            <button onClick={handleTerminer} className="btn btn-success btn-lg btn-block">
              <Square size={24} />
              Terminer l'intervention
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RealisationIntervention;

